import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import {
  Lightbulb, Plus, Search, ChevronDown, ChevronRight, X, Eye, BarChart3,
  Edit3, Trash2, Check, RefreshCw, Users, Smartphone, Save, ArrowLeft,
  Filter, AlertTriangle, TrendingUp, Loader2, Copy, Info,
} from "lucide-react";
import { toast } from "sonner";

// ── Tip List View ─────────────────────────────────────────────────
const TipsList = ({ tips, total, page, setPage, limit, onSelect, onRefresh, loading }) => {
  return (
    <div className="space-y-2" data-testid="tips-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">{total} tips total</p>
        <button onClick={onRefresh} className="text-xs text-sky-500 hover:text-sky-600 flex items-center gap-1" data-testid="tips-refresh-btn">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
      ) : (
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {tips.map((tip) => (
            <button
              key={tip.tip_id}
              onClick={() => onSelect(tip)}
              className="w-full text-left px-3 py-2.5 rounded-lg border border-slate-100 hover:border-sky-200 hover:bg-sky-50/50 transition-all group"
              data-testid={`tip-row-${tip.tip_id}`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tip.is_active !== false ? "bg-emerald-400" : "bg-slate-300"}`} />
                <span className="text-xs font-bold text-slate-700 flex-1 truncate">{tip.tip_id}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{tip.audience}</span>
                <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-sky-400 transition" />
              </div>
              <div className="flex items-center gap-2 mt-1 pl-4">
                <span className="text-[10px] text-slate-400 truncate">{tip.tip_key}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-100">{tip.page_key}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {total > limit && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 disabled:opacity-30">Prev</button>
          <span className="text-xs text-slate-400">Page {page} / {Math.ceil(total / limit)}</span>
          <button onClick={() => setPage(page + 1)} disabled={page * limit >= total} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
};

// ── Mobile Preview ────────────────────────────────────────────────
const TipPreview = ({ tip }) => {
  const steps = tip?.steps || [];
  const firstStep = steps[0] || {};
  const variations = firstStep._variations || [];
  const previewText = variations[0] || `[${firstStep.copy_bank_key || "no copy"}]`;
  const isMultiStep = steps.length > 1;

  return (
    <div className="relative" data-testid="tip-mobile-preview">
      {/* Phone frame */}
      <div className="mx-auto w-[220px] rounded-[24px] bg-slate-900 p-2 shadow-xl border-2 border-slate-700">
        {/* Notch */}
        <div className="mx-auto w-16 h-4 bg-slate-800 rounded-b-xl mb-2" />
        {/* Screen */}
        <div className="bg-slate-950 rounded-xl p-3 min-h-[300px]">
          {/* Fake page header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 rounded-lg bg-slate-800" />
            <div className="flex-1">
              <div className="h-2 w-16 bg-slate-800 rounded" />
              <div className="h-1.5 w-10 bg-slate-800/50 rounded mt-1" />
            </div>
          </div>

          {/* Tip banner */}
          <div className="bg-gradient-to-r from-amber-500/15 to-amber-500/5 border border-amber-500/25 rounded-xl p-2.5 mb-3 relative">
            <button className="absolute top-1.5 right-1.5 p-0.5 text-white/30">
              <X className="w-2.5 h-2.5" />
            </button>
            <div className="flex items-start gap-2 pr-4">
              <div className="w-5 h-5 rounded-md bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                <Lightbulb className="w-2.5 h-2.5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[7px] font-bold text-amber-400/80 uppercase tracking-wider mb-0.5">
                  Tip of the Day
                  {isMultiStep && <span className="ml-1 text-white/30 normal-case">1/{steps.length}</span>}
                </p>
                <p className="text-[8px] text-white/70 leading-relaxed line-clamp-3">{previewText}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className="text-[7px] font-bold text-amber-400">
                    {tip?.is_entry_point ? "Show me" : isMultiStep ? "Next" : "Got it"}
                  </span>
                  <ChevronRight className="w-2 h-2 text-amber-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Fake page content */}
          <div className="space-y-2">
            <div className="h-8 bg-slate-800/50 rounded-lg" />
            <div className="h-12 bg-slate-800/30 rounded-lg" />
            <div className="h-6 bg-slate-800/20 rounded-lg w-3/4" />
          </div>
        </div>
      </div>

      {/* Meta info below phone */}
      <div className="mt-3 text-center space-y-1">
        <p className="text-[10px] text-slate-400">Page: <span className="font-mono text-slate-600">{tip?.page_key || "—"}</span></p>
        <p className="text-[10px] text-slate-400">
          Audience: <span className="text-slate-600">{tip?.audience || "—"}</span>
          {" | "}Points: <span className="text-amber-600">{tip?.points_reward || 0}</span>
        </p>
        {tip?.is_entry_point && (
          <p className="text-[10px] text-sky-500">Entry point &rarr; {tip.entry_point_destination_page}</p>
        )}
      </div>
    </div>
  );
};

// ── A/B Stats Panel ───────────────────────────────────────────────
const ABStatsPanel = ({ tipId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tipId) return;
    setLoading(true);
    axios.get(`${API}/admin/tips/${tipId}/ab-stats`)
      .then(res => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [tipId]);

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-sky-500" /></div>;
  if (!stats || !stats.length) return <p className="text-xs text-slate-400 text-center py-4">No A/B data yet</p>;

  const maxImpressions = Math.max(...stats.map(s => s.impressions), 1);

  return (
    <div className="space-y-1.5 max-h-[45vh] overflow-y-auto" data-testid="ab-stats-panel">
      <div className="grid grid-cols-[40px_1fr_60px_50px_55px_40px] gap-1 text-[9px] font-medium text-slate-400 px-1 sticky top-0 bg-white py-1">
        <span>Var</span><span>Text</span><span className="text-right">Impr</span><span className="text-right">Acts</span><span className="text-right">Rate</span><span className="text-center">Win</span>
      </div>
      {stats.map((s) => (
        <div key={s.variation_index} className={`grid grid-cols-[40px_1fr_60px_50px_55px_40px] gap-1 items-center px-1 py-1.5 rounded-md text-xs ${s.is_promoted ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50"}`}>
          <span className="text-slate-500 font-mono text-[10px]">V{s.variation_index}</span>
          <span className="text-slate-600 truncate text-[10px]" title={s.variation_text}>{s.variation_text || "—"}</span>
          <span className="text-right text-slate-700 font-medium">{s.impressions}</span>
          <span className="text-right text-sky-600">{s.actions}</span>
          <span className={`text-right font-bold ${s.action_rate > 20 ? "text-emerald-600" : s.action_rate > 10 ? "text-amber-600" : "text-slate-500"}`}>{s.action_rate}%</span>
          <span className="text-center">{s.is_promoted ? <Check className="w-3 h-3 text-emerald-500 mx-auto" /> : ""}</span>
        </div>
      ))}
      {/* Bar chart visualization */}
      <div className="mt-3 space-y-0.5">
        <p className="text-[10px] text-slate-400 font-medium mb-1">Impression Distribution</p>
        {stats.map(s => (
          <div key={s.variation_index} className="flex items-center gap-1.5">
            <span className="text-[9px] text-slate-400 w-5 text-right">{s.variation_index}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${s.is_promoted ? "bg-emerald-400" : "bg-sky-300"}`}
                style={{ width: `${(s.impressions / maxImpressions) * 100}%` }}
              />
            </div>
            <span className="text-[9px] text-slate-400 w-8 text-right">{s.impressions}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Page Keys Reference Panel ─────────────────────────────────────
const PageKeysPanel = ({ onSelect }) => {
  const [keys, setKeys] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/tips/page-keys`)
      .then(res => setKeys(res.data))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-1" data-testid="page-keys-panel">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Page Keys in Use</p>
      {keys.map(k => (
        <button
          key={k.page_key}
          onClick={() => onSelect(k.page_key)}
          className="w-full text-left px-2 py-1.5 rounded-md hover:bg-amber-50 border border-transparent hover:border-amber-200 transition text-xs flex items-center justify-between"
        >
          <span className="font-mono text-slate-600">{k.page_key}</span>
          <span className="text-[10px] text-slate-400">{k.count}</span>
        </button>
      ))}
    </div>
  );
};

// ── Tip Editor ────────────────────────────────────────────────────
const TipEditor = ({ tip, onSave, onCancel, onDelete }) => {
  const [form, setForm] = useState({
    tip_id: "",
    tip_key: "",
    audience: "both",
    page_key: "",
    is_entry_point: false,
    entry_point_destination_page: "",
    entry_point_part2_tip_id: "",
    steps: [{ step_number: 1, copy_bank_key: "", highlight_target: "", action_trigger: "", _variations: [] }],
    points_reward: 0,
    gamification_rule_key: "",
    is_active: true,
    schedule_start_date: "",
    schedule_end_date: "",
    food_holiday_id: "",
  });
  const [saving, setSaving] = useState(false);
  const isNew = !tip;

  useEffect(() => {
    if (tip) {
      setForm({
        tip_id: tip.tip_id || "",
        tip_key: tip.tip_key || "",
        audience: tip.audience || "both",
        page_key: tip.page_key || "",
        is_entry_point: tip.is_entry_point || false,
        entry_point_destination_page: tip.entry_point_destination_page || "",
        entry_point_part2_tip_id: tip.entry_point_part2_tip_id || "",
        steps: (tip.steps || []).map(s => ({
          step_number: s.step_number || 1,
          copy_bank_key: s.copy_bank_key || "",
          highlight_target: s.highlight_target || "",
          action_trigger: s.action_trigger || "",
          _variations: s._variations || [],
        })),
        points_reward: tip.points_reward || 0,
        gamification_rule_key: tip.gamification_rule_key || "",
        is_active: tip.is_active !== false,
        schedule_start_date: tip.schedule_start_date || "",
        schedule_end_date: tip.schedule_end_date || "",
        food_holiday_id: tip.food_holiday_id || "",
      });
    }
  }, [tip]);

  const updateField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const updateStep = (idx, key, val) => {
    setForm(prev => {
      const steps = [...prev.steps];
      steps[idx] = { ...steps[idx], [key]: val };
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    setForm(prev => ({
      ...prev,
      steps: [...prev.steps, { step_number: prev.steps.length + 1, copy_bank_key: "", highlight_target: "", action_trigger: "", _variations: [] }],
    }));
  };

  const removeStep = (idx) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 })),
    }));
  };

  const handleSave = async () => {
    if (!form.tip_id) { toast.error("tip_id is required"); return; }
    if (!form.page_key) { toast.error("page_key is required"); return; }
    setSaving(true);
    try {
      await onSave(form, isNew);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-sky-300 focus:ring-1 focus:ring-sky-100 bg-white";
  const labelCls = "text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-0.5 block";

  return (
    <div className="space-y-3" data-testid="tip-editor">
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="editor-back-btn">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="flex items-center gap-2">
          {!isNew && onDelete && (
            <button onClick={() => onDelete(form.tip_id)} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1" data-testid="tip-delete-btn">
              <Trash2 className="w-3 h-3" /> Deactivate
            </button>
          )}
          <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 rounded-lg bg-sky-500 text-white text-xs font-bold hover:bg-sky-600 disabled:opacity-50 flex items-center gap-1" data-testid="tip-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {isNew ? "Create" : "Update"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Tip ID</label>
          <input value={form.tip_id} onChange={e => updateField("tip_id", e.target.value)} disabled={!isNew} className={`${inputCls} ${!isNew ? "bg-slate-50 text-slate-400" : ""}`} placeholder="TIP-022" data-testid="input-tip-id" />
        </div>
        <div>
          <label className={labelCls}>Tip Key</label>
          <input value={form.tip_key} onChange={e => updateField("tip_key", e.target.value)} className={inputCls} placeholder="user_tip_new_feature" data-testid="input-tip-key" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Audience</label>
          <select value={form.audience} onChange={e => updateField("audience", e.target.value)} className={inputCls} data-testid="select-audience">
            <option value="user">User</option>
            <option value="vendor">Vendor</option>
            <option value="both">Both</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Page Key (free-form)</label>
          <input value={form.page_key} onChange={e => updateField("page_key", e.target.value)} className={inputCls} placeholder="discover_home" data-testid="input-page-key" />
        </div>
        <div>
          <label className={labelCls}>Points Reward</label>
          <input type="number" value={form.points_reward} onChange={e => updateField("points_reward", parseInt(e.target.value) || 0)} className={inputCls} data-testid="input-points" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Schedule Start</label>
          <input type="datetime-local" value={form.schedule_start_date ? form.schedule_start_date.slice(0, 16) : ""} onChange={e => updateField("schedule_start_date", e.target.value ? new Date(e.target.value).toISOString() : null)} className={inputCls} data-testid="input-schedule-start" />
        </div>
        <div>
          <label className={labelCls}>Schedule End</label>
          <input type="datetime-local" value={form.schedule_end_date ? form.schedule_end_date.slice(0, 16) : ""} onChange={e => updateField("schedule_end_date", e.target.value ? new Date(e.target.value).toISOString() : null)} className={inputCls} data-testid="input-schedule-end" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Gamification Rule Key</label>
          <input value={form.gamification_rule_key} onChange={e => updateField("gamification_rule_key", e.target.value)} className={inputCls} placeholder="pts_tip_action_..." data-testid="input-gam-rule" />
        </div>
        <div>
          <label className={labelCls}>Food Holiday ID</label>
          <input value={form.food_holiday_id} onChange={e => updateField("food_holiday_id", e.target.value)} className={inputCls} placeholder="optional" data-testid="input-food-holiday" />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={e => updateField("is_active", e.target.checked)} className="w-3.5 h-3.5 rounded accent-emerald-500" data-testid="checkbox-active" />
          <span className="text-xs text-slate-600">Active</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_entry_point} onChange={e => updateField("is_entry_point", e.target.checked)} className="w-3.5 h-3.5 rounded accent-sky-500" data-testid="checkbox-entry-point" />
          <span className="text-xs text-slate-600">Entry Point</span>
        </label>
      </div>

      {form.is_entry_point && (
        <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-sky-200">
          <div>
            <label className={labelCls}>Destination Page</label>
            <input value={form.entry_point_destination_page} onChange={e => updateField("entry_point_destination_page", e.target.value)} className={inputCls} placeholder="/vendor" data-testid="input-dest-page" />
          </div>
          <div>
            <label className={labelCls}>Part 2 Tip ID</label>
            <input value={form.entry_point_part2_tip_id} onChange={e => updateField("entry_point_part2_tip_id", e.target.value)} className={inputCls} placeholder="TIP-003B" data-testid="input-part2-id" />
          </div>
        </div>
      )}

      {/* Steps */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className={labelCls}>Steps ({form.steps.length})</label>
          <button onClick={addStep} className="text-[10px] text-sky-500 hover:text-sky-600 flex items-center gap-0.5" data-testid="add-step-btn">
            <Plus className="w-3 h-3" /> Add Step
          </button>
        </div>
        <div className="space-y-2">
          {form.steps.map((step, idx) => (
            <div key={idx} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-500">Step {idx + 1}</span>
                {form.steps.length > 1 && (
                  <button onClick={() => removeStep(idx)} className="text-red-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-slate-400">Copy Bank Key</label>
                  <input value={step.copy_bank_key} onChange={e => updateStep(idx, "copy_bank_key", e.target.value)} className={inputCls} placeholder="tip_user_new_feature" />
                </div>
                <div>
                  <label className="text-[9px] text-slate-400">Highlight Target</label>
                  <input value={step.highlight_target} onChange={e => updateStep(idx, "highlight_target", e.target.value)} className={inputCls} placeholder="[data-testid='...']" />
                </div>
              </div>
              <div>
                <label className="text-[9px] text-slate-400">Action Trigger</label>
                <input value={step.action_trigger} onChange={e => updateStep(idx, "action_trigger", e.target.value)} className={inputCls} placeholder="tap_feature" />
              </div>
              <div>
                <label className="text-[9px] text-slate-400">Variations (15 lines, one per variation)</label>
                <textarea
                  value={(step._variations || []).join("\n")}
                  onChange={e => updateStep(idx, "_variations", e.target.value.split("\n"))}
                  className={`${inputCls} min-h-[60px] font-mono text-[10px]`}
                  placeholder="Variation 1&#10;Variation 2&#10;..."
                  rows={4}
                />
                <p className="text-[9px] text-slate-400 mt-0.5">{(step._variations || []).filter(v => v.trim()).length}/15 variations</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Analytics Overview ────────────────────────────────────────────
const AnalyticsOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/tips/analytics/summary`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-sky-500" /></div>;
  if (!data) return <p className="text-xs text-slate-400 text-center">Failed to load analytics</p>;

  const { summary, tips } = data;

  return (
    <div className="space-y-3" data-testid="tips-analytics-overview">
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-white rounded-xl border border-slate-100 p-3 text-center">
          <p className="text-xl font-black text-slate-800">{summary.total_tips}</p>
          <p className="text-[10px] text-slate-400">Total Tips</p>
        </div>
        <div className="bg-white rounded-xl border border-emerald-100 p-3 text-center">
          <p className="text-xl font-black text-emerald-600">{summary.active_tips}</p>
          <p className="text-[10px] text-emerald-500">Active</p>
        </div>
        <div className="bg-white rounded-xl border border-sky-100 p-3 text-center">
          <p className="text-xl font-black text-sky-600">{summary.total_impressions}</p>
          <p className="text-[10px] text-sky-500">Impressions</p>
        </div>
        <div className="bg-white rounded-xl border border-amber-100 p-3 text-center">
          <p className="text-xl font-black text-amber-600">{summary.overall_action_rate}%</p>
          <p className="text-[10px] text-amber-500">Action Rate</p>
        </div>
      </div>

      <div className="space-y-1 max-h-[50vh] overflow-y-auto">
        <div className="grid grid-cols-[1fr_80px_60px_50px_50px] gap-1 text-[9px] font-medium text-slate-400 px-1 sticky top-0 bg-white py-1">
          <span>Tip</span><span>Page</span><span className="text-right">Impr</span><span className="text-right">Acts</span><span className="text-right">Rate</span>
        </div>
        {tips.map(t => (
          <div key={t.tip_id} className={`grid grid-cols-[1fr_80px_60px_50px_50px] gap-1 items-center px-1 py-1.5 rounded-md text-xs ${!t.is_active ? "opacity-40" : "hover:bg-slate-50"}`}>
            <div className="truncate">
              <span className="font-bold text-slate-700">{t.tip_id}</span>
              <span className="text-[10px] text-slate-400 ml-1">{t.audience}</span>
            </div>
            <span className="text-[10px] text-slate-500 truncate">{t.page_key}</span>
            <span className="text-right text-slate-700">{t.total_impressions}</span>
            <span className="text-right text-sky-600">{t.total_actions}</span>
            <span className={`text-right font-bold ${t.action_rate > 20 ? "text-emerald-600" : t.action_rate > 10 ? "text-amber-600" : "text-slate-500"}`}>{t.action_rate}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MAIN: TipsManagementPanel
// ════════════════════════════════════════════════════════════════
export default function TipsManagementPanel() {
  const [view, setView] = useState("list"); // list | editor | analytics
  const [tips, setTips] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedTip, setSelectedTip] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const LIMIT = 50;

  const fetchTips = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (statusFilter) params.append("status", statusFilter);
      if (audienceFilter) params.append("audience", audienceFilter);
      if (searchQuery) params.append("search", searchQuery);
      const res = await axios.get(`${API}/admin/tips?${params}`);
      setTips(res.data.tips);
      setTotal(res.data.total);
    } catch { toast.error("Failed to load tips"); }
    finally { setLoading(false); }
  }, [page, statusFilter, audienceFilter, searchQuery]);

  useEffect(() => { fetchTips(); }, [fetchTips]);

  const handleSelectTip = async (tip) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tips/${tip.tip_id}`);
      setSelectedTip(res.data);
      setView("editor");
    } catch { toast.error("Failed to load tip details"); }
    finally { setDetailLoading(false); }
  };

  const handleSave = async (form, isNew) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/tips`, form);
        toast.success(`Tip ${form.tip_id} created`);
      } else {
        await axios.put(`${API}/admin/tips/${form.tip_id}`, form);
        toast.success(`Tip ${form.tip_id} updated`);
      }
      setView("list");
      setSelectedTip(null);
      fetchTips();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async (tipId) => {
    if (!window.confirm(`Deactivate tip ${tipId}?`)) return;
    try {
      await axios.delete(`${API}/admin/tips/${tipId}`);
      toast.success(`Tip ${tipId} deactivated`);
      setView("list");
      setSelectedTip(null);
      fetchTips();
    } catch { toast.error("Deactivation failed"); }
  };

  const handleNewTip = () => {
    setSelectedTip(null);
    setView("editor");
  };

  // ── Tab bar ──
  const tabs = [
    { id: "list", label: "Tips List", icon: Lightbulb },
    { id: "editor", label: selectedTip ? `Edit ${selectedTip.tip_id}` : "New Tip", icon: Edit3 },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
  ];

  return (
    <div className="space-y-4" data-testid="tips-management-panel">
      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-slate-100 pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setView(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${view === tab.id ? "bg-sky-50 text-sky-600 border border-sky-200" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={handleNewTip} className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 flex items-center gap-1" data-testid="new-tip-btn">
          <Plus className="w-3 h-3" /> New Tip
        </button>
      </div>

      {/* List View */}
      {view === "list" && (
        <div>
          {/* Filters bar */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                placeholder="Search tip_id, tip_key, page_key..."
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-sky-300"
                data-testid="tips-search-input"
              />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`px-2 py-1.5 rounded-lg border text-xs flex items-center gap-1 ${showFilters ? "bg-sky-50 border-sky-200 text-sky-600" : "border-slate-200 text-slate-500"}`} data-testid="tips-filter-toggle">
              <Filter className="w-3 h-3" /> Filters
            </button>
          </div>

          {showFilters && (
            <div className="flex items-center gap-3 mb-3 px-2 py-2 bg-slate-50 rounded-lg">
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="text-xs border border-slate-200 rounded-lg px-2 py-1" data-testid="filter-status">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select value={audienceFilter} onChange={e => { setAudienceFilter(e.target.value); setPage(1); }} className="text-xs border border-slate-200 rounded-lg px-2 py-1" data-testid="filter-audience">
                <option value="">All Audiences</option>
                <option value="user">User</option>
                <option value="vendor">Vendor</option>
                <option value="both">Both</option>
              </select>
              {(statusFilter || audienceFilter) && (
                <button onClick={() => { setStatusFilter(""); setAudienceFilter(""); setPage(1); }} className="text-[10px] text-red-400 hover:text-red-500">Clear</button>
              )}
            </div>
          )}

          <div className="grid grid-cols-[1fr_220px] gap-4">
            <TipsList
              tips={tips} total={total} page={page} setPage={setPage} limit={LIMIT}
              onSelect={handleSelectTip} onRefresh={fetchTips} loading={loading || detailLoading}
            />
            <div className="space-y-4">
              <PageKeysPanel onSelect={(key) => { setSearchQuery(""); setAudienceFilter(""); setStatusFilter(""); setPage(1); /* Filter by page_key through URL params would be nice but for now just search */ setSearchQuery(key); }} />
            </div>
          </div>
        </div>
      )}

      {/* Editor View */}
      {view === "editor" && (
        <div className="grid grid-cols-[1fr_240px] gap-4">
          <TipEditor
            tip={selectedTip}
            onSave={handleSave}
            onCancel={() => { setView("list"); setSelectedTip(null); }}
            onDelete={selectedTip ? handleDelete : null}
          />
          <div className="space-y-4">
            <TipPreview tip={selectedTip || { steps: [{ copy_bank_key: "", _variations: ["Your tip text here..."] }], page_key: "preview", audience: "both", points_reward: 0 }} />
            {selectedTip && (
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">A/B Performance</p>
                <ABStatsPanel tipId={selectedTip.tip_id} />
              </div>
            )}
            <PageKeysPanel onSelect={(key) => {
              // Fill in page_key input
              const el = document.querySelector('[data-testid="input-page-key"]');
              if (el) {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
                nativeInputValueSetter.call(el, key);
                el.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }} />
          </div>
        </div>
      )}

      {/* Analytics View */}
      {view === "analytics" && <AnalyticsOverview />}
    </div>
  );
}
